import { createRouter, createWebHistory } from 'vue-router';
const projectName = process.env.VUE_APP_PROJECT || 'motom'; // default project is 'motom'
import store from '@/store/index.js';

// ============================================================================
// Helper Functions for Route Guards
// ============================================================================

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
function isAuthenticated() {
  const userData = store.state.user_data;
  return !!(userData && userData.id && userData.status === 100);
}

/**
 * Check if user has a merchant account
 * @returns {boolean} True if user has merchant account
 */
function hasMerchantAccount() {
  const userData = store.state.user_data;
  return !!(userData && userData.merchant_account && userData.merchant_account_id);
}

/**
 * Check if user has a specific permission
 * @param {string} category - Permission category (e.g., 'analytics', 'settingWorkspace')
 * @param {string} permissionName - Permission name (e.g., 'viewPerformanceSummaries')
 * @returns {boolean} True if user has the permission
 */
function checkPermission(category, permissionName) {
  const permissions = store.state.user_data?.permission_list;
  return permissions?.[category]?.[permissionName]?.permission !== 0;
}

/**
 * Check if user can access a route based on its meta properties
 * @param {Object} route - Route object
 * @returns {boolean} True if user can access the route
 */
function canAccessRoute(route) {
  const meta = route.meta || {};

  if (!meta.requiresAuth) return true;
  if (!isAuthenticated()) return false;
  if (meta.requiresMerchant && !hasMerchantAccount()) return false;

  // Check permissions if specified
  if (meta.permissions) {
    return meta.permissions.every(p => checkPermission(p.category, p.name));
  }

  return true;
}

/**
 * Check if user can access a specific settings type
 * Used for dynamic settings routes (/settings/:type)
 * 
 * This function implements the same permission logic as if we had separate routes:
 * - /settings/account: requiresAuth: true, requiresMerchant: false
 * - /settings/team: requiresAuth: true, requiresMerchant: true
 * - /settings/team-setting: requiresAuth: true, requiresMerchant: true, permissions: [settingWorkspace.editRolePermissions]
 * - /settings/company: requiresAuth: true, requiresMerchant: true, permissions: [settingWorkspace.manageSettings]
 * - /settings/domain-edit: requiresAuth: true, requiresMerchant: true, permissions: [settingIntegrations.integrations]
 * - /settings/script: requiresAuth: true, requiresMerchant: true, permissions: [settingIntegrations.integrations]
 * - /settings/storefront-img, content-listening, permissions, term-setting: requiresAuth: true, requiresMerchant: true
 * 
 * @param {string} type - Settings type (e.g., 'account', 'company', 'team-setting')
 * @returns {boolean} True if user can access the settings type
 */
function canAccessSettingsType(type) {
  const permissions = store.state.user_data?.permission_list;
  
  // Settings types that require specific permissions (equivalent to meta.permissions in route config)
  const settingsPermissions = {
    'company': { category: 'settingWorkspace', name: 'manageSettings' },
    'team-setting': { category: 'settingWorkspace', name: 'teamSettings' },
    'domain-edit': { category: 'settingIntegrations', name: 'integrations' },
    'script': { category: 'settingIntegrations', name: 'integrations' },
    'term-setting': {category:'settingIntegrations', name:'privacySettings' }
  };

  // Settings types that require merchant account (equivalent to meta.requiresMerchant: true)
  const requiresMerchant = [
    'team', 'company', 'team-setting', 
    'domain-edit', 'script', 
    'storefront-img', 'content-listening', 'permissions', 'term-setting'
  ];

  // Check if merchant account is required
  if (requiresMerchant.includes(type) && !hasMerchantAccount()) {
    return false;
  }

  // Check specific permission if required
  if (settingsPermissions[type]) {
    const { category, name } = settingsPermissions[type];
    return checkPermission(category, name);
  }

  // Default allow for account and other settings (equivalent to requiresMerchant: false)
  return true;
}

/**
 * Get the appropriate redirect route based on user state
 * @param {Object} userData - User data from store
 * @returns {string} Redirect route path
 */
function getRedirectRoute(userData) {
  if (!isAuthenticated()) return '/login';
  else if (!hasMerchantAccount()) return '/landing';
  else if (userData?.is_first_login === 1) return '/onboard';
  else if (userData?.merchant_account?.enable_creators_count > 0) return '/creator/list';
  else return '/';
}

function loginComponent() {
  // return () => import(/* webpackChunkName: "login" */ '../views/Login.vue');

  if (projectName === 'sephora') {
    return () => import(/* webpackChunkName: "login" */ '../views/LoginSso.vue');
  } else {
    return () => import(/* webpackChunkName: "login" */ '../views/Login.vue');
  }
}

const routes = [
  {
    path: '/',
    name: 'home',
    meta: {
      title: 'Anchor',
      // requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "home" */ '../views/Home.vue'),
  },

  {
    path: '/login',
    name: 'login',
    meta: {
      title: 'Login | Anchor',
      requiresAuth: false,
    },
    component: loginComponent(),
  },

  {
    path: '/logout',
    name: 'logout',
    meta: {
      title: 'Logout | Anchor',
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "logout" */ '../views/Logout.vue'),
  },

  {
    path: '/reset-password',
    name: 'reset-password',
    meta: {
      title: 'Reset Password | Anchor',
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "reset-password" */ '../views/ResetPassword.vue'),
  },

  {
    path: '/forgot-password/verify',
    name: 'forgot-password',
    meta: {
      title: 'Forgot Password | Anchor',
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "forgot-password" */ '../views/ForgotPassword.vue'),
  },

  {
    path: '/onboard',
    name: 'onboard',
    meta: {
      title: 'Onboard | Anchor',
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "onboard" */ '../views/Onboard.vue'),
  },

  {
    path: '/landing',
    name: 'landing',
    meta: {
      title: 'Landing | Anchor',
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "landing" */ '../views/Landing.vue'),
  },

  {
    path: '/creator/get-started',
    name: 'creator--get-started',
    meta: {
      title: 'Creator Management | Anchor',
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "creator-get-started" */ '../views/creator/GetStarted.vue'),
  },

  {
    path: '/creator/list',
    name: 'creator--list',
    meta: {
      title: 'Creator Management | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
    },
    component: () => import(/* webpackChunkName: "creator-list" */ '../views/creator/List.vue'),
  },

  {
    path: '/programs/applications-list',
    name: 'programs--applications-list',
    meta: {
      title: 'Applications | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
    },
    component: () => import(/* webpackChunkName: "creator-list" */ '../views/programs/ApplicationsList.vue'),
  },

  {
    path: '/programs/application/create/:application_id',
    name: 'programs--application-create',
    meta: {
      title: 'Application | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'recruitment', name: 'createApplications' }],
    },
    component: () => import(/* webpackChunkName: "creator-list" */ '../views/programs/FormSetting.vue'),
  },

  {
    path: '/programs/application/:type/:application_id',
    name: 'programs--application-edit',
    meta: {
      title: 'Application | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'recruitment', name: 'manageApplications' }],
    },
    component: () => import(/* webpackChunkName: "creator-list" */ '../views/programs/FormSetting.vue'),
  },

  {
    path: '/programs/application/:application_id',
    name: 'programs--application',
    meta: {
      title: 'Application | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'recruitment', name: 'manageApplications' }],
    },
    component: () => import(/* webpackChunkName: "creator-list" */ '../views/programs/Application.vue'),
  },

  {
    path: '/analytics/overview',
    name: 'analytics--overview',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-overview" */ '../views/analytics/Overview.vue'),
  },

  {
    path: '/analytics/products',
    name: 'analytics--products',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-products" */ '../views/analytics/Products.vue'),
  },

  {
    path: '/analytics/content',
    name: 'analytics--content',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-content" */ '../views/analytics/Content.vue'),
  },

  {
    path: '/analytics/collections',
    name: 'analytics--collections',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-collections" */ '../views/analytics/Collections.vue'),
  },

  {
    path: '/analytics/posts',
    name: 'analytics--posts',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-posts" */ '../views/analytics/Posts.vue'),
  },

  {
    path: '/analytics/creator',
    name: 'analytics--creator',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-creator" */ '../views/analytics/Creator.vue'),
  },

  {
    path: '/analytics/brands',
    name: 'analytics--brands',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-brands" */ '../views/analytics/Brands.vue'),
  },

    {
    path: '/analytics/advertisers',
    name: 'analytics--advertisers',
    meta: {
      title: 'Analytics | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'analytics', name: 'viewPerformanceSummaries' }],
    },
    component: () => import(/* webpackChunkName: "analytics-brands" */ '../views/analytics/Advertisers.vue'),
  },

  {
    path: '/request-a-demo',
    name: 'request-for-demo',
    meta: {
      title: 'Request for Demo | Anchor',
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "request-for-demo" */ '../views/RequestForDemo.vue'),
  },

  {
    path: '/messaging/announcements',
    name: 'messaging-announcements',
    meta: {
      title: 'Announcements | Messaging | Anchor',
      menu_meta: 'messaging',
      menu_sub: 'announcements',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'messagesAndAnnouncements', name: 'manageAnnouncements' }],
    },
    component: () => import(/* webpackChunkName: "announcements" */ '../views/messaging/Announcements.vue'),
  },

  {
    path: '/messaging/announcements/:id',
    name: 'messaging-announcements-detail',
    meta: {
      title: 'Announcements | Messaging | Anchor',
      menu_meta: 'messaging',
      menu_sub: 'announcements',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'messagesAndAnnouncements', name: 'manageAnnouncements' }],
    },
    component: () => import(/* webpackChunkName: "announcements" */ '../views/messaging/Announcements.vue'),
  },

  {
    path: '/messaging/chats',
    name: 'messaging-chats',
    meta: {
      title: 'Chats | Messaging | Anchor',
      menu_meta: 'messaging',
      menu_sub: 'chats',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'messagesAndAnnouncements', name: 'manageChat' }],
    },
    component: () => import(/* webpackChunkName: "chats" */ '../views/messaging/Chats.vue'),
  },

  {
    path: '/messaging/chats/:id',
    name: 'messaging-chats-detail',
    meta: {
      title: 'Chats | Messaging | Anchor',
      menu_meta: 'messaging',
      menu_sub: 'chats',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'messagesAndAnnouncements', name: 'manageChat' }],
    },
    component: () => import(/* webpackChunkName: "chats" */ '../views/messaging/Chats.vue'),
  },

  {
    path: '/setting',
    name: 'setting',
    meta: {
      title: 'Setting | Anchor',
      requiresAuth: true,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "setting" */ '../views/Setting.vue'),
  },

  {
    path: '/settings/custom',
    name: 'setting-custom',
    meta: { 
      title: 'Setting | Anchor',
      requiresAuth: true,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "setting-desjgn" */ '../views/SettingsDesign.vue'),
  },

  {
    path: '/settings/storefront',
    name: 'setting-storefront',
    meta: { 
      title: 'Setting | Anchor',
      requiresAuth: true,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "setting-desjgn" */ '../views/SettingsDesign.vue'),
  },

  // ============================================================================
  // Settings Routes - Dynamic routes with permission checks
  // ============================================================================
  // Uses dynamic :type and :subtype params instead of individual routes to preserve params in components
  // Permission checks are performed in router.beforeEach() via canAccessSettingsType(type)
  // This approach is equivalent to having separate routes with different meta.requiresMerchant and meta.permissions
  // See canAccessSettingsType() function for detailed permission mapping for each type
  {
    path: '/settings/:type',
    name: 'settings',
    meta: {
      title: 'Settings | Anchor',
      requiresAuth: true,
      requiresMerchant: false, // Dynamically checked based on :type in router guard
    },
    component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
  },
  {
    path: '/settings/:type/:subtype',
    name: 'settings-subtype',
    meta: {
      title: 'Settings | Anchor',
      requiresAuth: true,
      requiresMerchant: false, // Dynamically checked based on :type in router guard
    },
    component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
  },

  {
    path: '/help',
    name: 'help',
    meta: {
      title: 'Help | Anchor',
      requiresAuth: false,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "help" */ '../views/Help.vue'),
  },

  {
    path: '/account-billing',
    name: 'account-billing',
    meta: {
      title: 'Account Billing | Anchor',
      requiresAuth: true,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "help" */ '../views/AccountBilling.vue'),
  },

  {
    path: '/pages/:slug',
    name: 'pages',
    meta: {
      title: 'Page | Anchor',
      requiresAuth: false,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "pages" */ '../views/Pages.vue'),
  },

  {
    path: '/:catchAll(.*)',
    name: 'not-found',
    meta: {
      title: 'Page Not Found | Anchor',
      requiresAuth: false,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFound.vue'),
  },

  {
    path: '/reports-list',
    name: 'reports',
    meta: {
      title: 'Reports | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
      permissions: [{ category: 'reports', name: 'downloadReports' }],
    },
    component: () => import(/* webpackChunkName: "settings" */ '../views/Reports.vue'),
  },

  //storefront
  {
    path: '/storefront/:id',
    name: 'storefront',
    meta: { 
      title: 'Storefront | Anchor',
      requiresAuth: true,
      requiresMerchant: true,
    },
    component: () => import(/* webpackChunkName: "settings" */ '../views/StorefrontCuration.vue')
  },

  {
    path: projectName === 'cj' ? '/creator-academy' : '/creator-hq',
    name: projectName === 'cj' ? 'creator-academy' : 'creator-hq',
    meta: {
      title: projectName === 'cj' ? 'Creator Academy | Anchor' : 'Creator HQ | Anchor',
      requiresAuth: true,
      requiresMerchant: false,
    },
    component: () => import(/* webpackChunkName: "creator-hq" */ '../views/CreatorAcademy.vue'),
  }

  // {
  //   path:'/login-oauth-register',
  //   name:'login-oauth-register',
  //   meta:{
  //     title:'Login | Anchor',
  //     requiresAuth: false,
  //     requiresMerchant: false,
  //   },
  //   component: () => import(/* webpackChunkName: "login-oauth-register" */ '../views/LoginHandleInvitePage.vue'),
  // },
];

let base_url = '/';

const router = createRouter({
  history: createWebHistory(base_url),
  routes,
});

/**
 * Promise that resolves when user data is loaded
 * Used to ensure router guards wait for initial user data fetch
 */
let userDataLoaded = false;
let userDataPromise = null;

/**
 * Initialize user data loading promise
 * This should be called when app starts loading user data
 */
export function initUserDataLoading() {
  if (!userDataPromise) {
    userDataPromise = new Promise((resolve) => {
      const checkUserData = () => {
        if (store.state.user_data !== false) {
          userDataLoaded = true;
          resolve();
        } else {
          setTimeout(checkUserData, 50);
        }
      };
      checkUserData();
    });
  }
  return userDataPromise;
}

/**
 * Wait for user data to be loaded if there's an access token
 * @returns {Promise<void>}
 */
async function ensureUserDataLoaded() {
  const hasToken = window.localStorage.getItem('access_token') !== null;
  
  // If there's a token and user data hasn't loaded yet, wait for it
  if (hasToken && !userDataLoaded) {
    initUserDataLoading();
    await userDataPromise;
  }
}

/**
 * Global router guard for authentication and permission checks
 */
router.beforeEach(async (to, from, next) => {
  // Wait for user data to load if needed
  await ensureUserDataLoaded();
  
  const meta = to.meta || {};
  const userData = store.state.user_data;

  // Redirect authenticated users away from login page
  if (to.name === 'login' && isAuthenticated()) {
    next(getRedirectRoute(userData));
    return;
  }

  // Public pages - allow direct access
  const publicPages = ['login', 'logout', 'forgot-password', 'reset-password', 'pages', 'not-found', 'request-for-demo', 'login-oauth-register'];
  if (publicPages.includes(to.name)) {
    next();
    return;
  }

  // Authentication check
  if (meta.requiresAuth && !isAuthenticated()) {
    console.warn('[Router] Not authenticated, redirecting to login');
    next('/login');
    return;
  }

  // Merchant account check
  if (meta.requiresMerchant && !hasMerchantAccount()) {
    console.warn('[Router] Merchant account required, redirecting to landing');
    next('/landing');
    return;
  }

  // First-time login redirect
  if (userData?.is_first_login === 1 && to.name !== 'onboard') {
    console.warn('[Router] First login, redirecting to onboard');
    next('/onboard');
    return;
  }

  // Prevent onboarded users from accessing onboard page
  if (userData?.is_first_login === 0 && to.name === 'onboard') {
    next('/');
    return;
  }

  // Settings permission check for generic routes
  if ((to.name === 'settings' || to.name === 'settings-subtype') && to.params.type) {
    if (!canAccessSettingsType(to.params.type)) {
      console.warn('[Router] No permission for settings type:', to.params.type);
      next('/settings/account'); // Redirect to account settings which is always accessible
      return;
    }
  }

  // Permission check
  if (!canAccessRoute(to)) {
    console.warn('[Router] Permission denied for:', to.path);
    const fallbackRoute = getRedirectRoute(userData);
    
    // Prevent infinite redirect
    if (to.path === fallbackRoute) {
      console.error('[Router] Infinite redirect detected');
      next('/');
      return;
    }
    
    next(fallbackRoute);
    return;
  }

  next();
});

/**
 * Update page title after navigation
 */
router.afterEach((to) => {
  if (to.meta?.title) {
    document.title = to.meta.title || 'Anchor';
  }
});

export default router;
